/**
 * "Save for everyone" against the dashboard's saved view: whether to ask
 * "Save your filters too?", and what each answer stores.
 *
 * Kept out of the route so the rules are tested without one.
 */
import { type DashboardDefinition, type FilterNode, isFilterGroup } from '@clockwise/semantic-core';
import type { DateRangeValue } from '@clockwise/shared/date-range';
import { toDefaultFilters } from '@/lib/filter-url';
import { changedView, type SavedView, type ViewChange } from '@/lib/saved-view';

/**
 * The question to ask, or `null` to save without asking.
 *
 * A field PINNED in this edit is left out of it: a chip can only be pinned
 * once it holds a value, and that value is how the pin was made, not a default
 * anyone chose. Its saved rules, if any, are carried through unchanged.
 */
export function dashboardSaveQuestion(input: {
  draft: DashboardDefinition;
  saved: DashboardDefinition;
  filters: readonly FilterNode[];
  period: DateRangeValue;
}): (SavedView & { changed: ViewChange }) | null {
  const wasPinned = new Set(input.saved.filters.map((decl) => decl.dimension));
  const pinnedNow = new Set(
    input.draft.filters.map((decl) => decl.dimension).filter((d) => !wasPinned.has(d)),
  );
  const onNewPin = (node: FilterNode) => !isFilterGroup(node) && pinnedNow.has(node.field);
  const change = changedView({
    filters: input.filters.filter((node) => !onNewPin(node)),
    period: input.period,
    savedFilters: input.draft.defaultFilters.filter((node) => !onNewPin(node)),
    savedPeriod: input.draft.period,
  });
  if (!change) return null;
  return {
    ...change,
    filters: [...change.filters, ...input.draft.defaultFilters.filter(onNewPin)],
  };
}

/** "Save my filters": the draft, opening on `view`. Rules alone — a dashboard's bar cannot make a group. */
export function withDefaultView(draft: DashboardDefinition, view: SavedView): DashboardDefinition {
  return { ...draft, defaultFilters: toDefaultFilters(view.filters), period: view.period };
}
